import EventDispatcher from "../event-dispatcher";
import UnitEditorApp from "../apps/unit-editor/unit-editor-app";
import logging from '../logging';
import { Object3D, OrthographicCamera, Vector2 } from "three";
import { s2w } from "../view3d";
import { registerListener } from "../html/register-listener";

const log = logging('tools');

/**
 * The distance in pixels the pointer must move before it's considered a drag.
 * If we treated this as zero, we'd get a lot of drags that were meant to be
 * clicks. This dispatches the events dragstart, drag, dragend, and click. The
 * click event should never occur if any of the drag events occur.
 */
const THRESHOLD = 5.0;

/**
 * This helper tracks mouse clicks on the main canvas and decides whether they
 * are click-and-drag events or simple click events.
 */
export default class DragDistanceHelper extends EventDispatcher {

    private app: UnitEditorApp;
    start: Vector2 = new Vector2();
    end: Vector2 = new Vector2();
    private moved: boolean = false;
    private _hit?: Object3D;
    private _current: Vector2 = new Vector2();

    constructor(app: UnitEditorApp) {
        super();
        this.app = app;
    }

    /**
     * Get the distance the mouse has traveled, measured in pixels.
     */
    get distance(): number {
        return this.start.distanceTo(this.end);
    }

    /**
     * Get the signed vector representing the total displacement of the pointer
     * since it was set down.
     */
    get delta(): Vector2 {
        const delta = new Vector2();
        return delta.subVectors(this.end, this.start);
    }

    /**
     * If using an orthographic camera, and assuming it's looking directly at
     * the XY plane, then this will contain the current mouse position in world
     * coordinates. These assumptions hold in the front view of the unit
     * editor.
     */
    get current(): Vector2 {
        return this._current.clone();
    }

    /**
     * The object that was hit on the pointerdown event. It's necessary to
     * check this rather than the application picker because the initial
     * dragstart event may have moved off this object.
     */
    get hit(): Object3D {
        return this._hit;
    }

    enable() {
        log.debug('DragDistanceHelper.enable()');
        registerListener(this.canvas, 'pointerdown', this.pointerdown);
    }

    disable() {
        log.debug('DragDistanceHelper.disable()');
        this.canvas.removeEventListener('pointerdown', this.pointerdown);
        this.canvas.removeEventListener('pointermove', this.pointermove);
        window.removeEventListener('pointerup', this.pointerup);
        this.start.set(0, 0);
        this.end.set(0, 0);
        this.moved = false;
    }

    private get canvas(): HTMLCanvasElement {
        return this.app.pipeline.canvas;
    }

    private pointerdown = (event: PointerEvent) => {
        log.debug('DragDistanceHelper.pointerdown()');
        if(!event.isPrimary
           || (event.pointerType == "mouse" && event.button != 0))
        {
            return;
        }
        this.start.set(event.offsetX, event.offsetY);
        registerListener(this.canvas, 'pointermove', this.pointermove);
        window.addEventListener('pointerup', this.pointerup, {once: true});
        this._hit = this.app.picker.topHit;
    }

    private pointermove = (event: MouseEvent) => {
        //log.debug('DragDistanceHelper.pointermove()');

        if(this.app.pipeline.camera instanceof OrthographicCamera) {
            const camera = this.app.pipeline.camera as OrthographicCamera;
            s2w(camera, event, this._current);
        }

        this.end.set(event.offsetX, event.offsetY);
        if(!this.moved && this.distance > THRESHOLD) {
            this.moved = true;
            log.debug('DragDistanceHelper trigger dragstart');
            this.trigger('dragstart');
        }

        if(this.moved) {
            log.debug('DragDistanceHelper trigger drag');
            this.trigger('drag');
        }
    }

    private pointerup = (event: MouseEvent) => {
        if(this.moved) {
            log.debug('DragDistanceHelper.pointerUp() triggering dragend');
            this.trigger('dragend');
        } else {
            log.debug('DragDistanceHelper.pointerUp() triggering click');
            this.trigger('click', event);
        }
        this.moved = false;
        this.start.set(0, 0);
        this.end.set(0, 0);
        this.canvas.removeEventListener('pointermove', this.pointermove);
        delete this._hit;
    }

}
